import { Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Empty, ErrorMessage, Loading, PageHeader, Pagination } from '../components';
import { SearchSelect } from '../SearchSelect';
import type { AuditEvent } from '../types';

const entities: Record<string, string> = {
  COMPANY: 'Kunde',
  CONTACT: 'Kontakt',
  SITE: 'Standort',
  DEVICE: 'Gerät',
  EXTERNAL_IDENTITY: 'Externe Verknüpfung',
  INTEGRATION: 'Integration',
  INTEGRATION_INSTANCE: 'Integrationsinstanz',
  MEMBERSHIP: 'Kundenzuordnung',
  ASSIGNMENT: 'Anwendungsfreigabe',
  LIFECYCLE_OPERATION: 'Lebenszyklus',
  WEBHOOK_SUBSCRIPTION: 'Webhook',
};
const verbs: Record<string, string> = {
  created: 'angelegt',
  updated: 'aktualisiert',
  archived: 'archiviert',
  deleted: 'entfernt',
  domain_added: 'Domain hinzugefügt',
  domain_removed: 'Domain entfernt',
  identity_added: 'Kontaktweg hinzugefügt',
  identity_updated: 'Kontaktweg geändert',
  identity_revoked: 'Kontaktweg widerrufen',
  identity_verified: 'Kontaktweg verifiziert',
  merged: 'zusammengeführt',
  merge_target_updated: 'Zusammenführung aktualisiert',
  anonymized: 'anonymisiert',
  reported: 'Status gemeldet',
  requested: 'angefordert',
  ticket_reconcile_requested: 'Synchronisierung angefordert',
  enabled: 'freigegeben',
  disabled: 'deaktiviert',
  device_desk_reported: 'DeviceDesk-Zustand übernommen',
};
export function auditLabel(event: AuditEvent) {
  const verb = event.action.split('.').at(-1) ?? '';
  return `${entities[event.entityType] ?? event.entityType} ${verbs[verb] ?? event.action}`;
}
const fields: Record<string, string> = {
  name: 'Name',
  firstName: 'Vorname',
  lastName: 'Nachname',
  displayName: 'Anzeigename',
  legalName: 'Rechtlicher Name',
  jobTitle: 'Position',
  notes: 'Notizen',
  status: 'Status',
  domains: 'Domains',
  identities: 'Kontaktwege',
  memberships: 'Kundenzuordnung',
  companyId: 'Kunde',
  siteId: 'Standort',
  responsibleContactId: 'Verantwortlicher Kontakt',
  archivedAt: 'Archivierung',
  category: 'Kategorie',
  hostname: 'Hostname',
  manufacturer: 'Hersteller',
  model: 'Modell',
  serialNumber: 'Seriennummer',
  assetTag: 'Inventarnummer',
  deviceType: 'Gerätetyp', execution: 'Ausführung', hosting: 'Betriebsort', formFactor: 'Bauform', roles: 'Rollen',
  deviceDesk: 'DeviceDesk-Zustand', 'deviceDesk.revokedAt': 'DeviceDesk-Widerruf',
};
export function auditFields(event: AuditEvent) {
  return event.changedFields?.map((field) => fields[field] ?? field).join(', ');
}

export function ActivityPage() {
  const [items, setItems] = useState<AuditEvent[]>([]);
  const [error, setError] = useState<unknown>();
  const [actor, setActor] = useState('');
  const [entityType, setEntityType] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [action, setAction] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      const query = new URLSearchParams({ page: String(page), pageSize: '25' });
      if (actor) query.set('actorId', actor);
      if (entityType) query.set('entityType', entityType);
      if (action) query.set('action', action);
      void api<{ items: AuditEvent[]; total: number }>(`/api/audit?${query}`, {
        signal: controller.signal,
      })
        .then((result) => {
          setItems(result.items);
          setTotal(result.total);
          setError(undefined);
        })
        .catch((caught) => {
          if (!controller.signal.aborted) setError(caught);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [actor, entityType, page, action, refresh]);
  return (
    <>
      <PageHeader title="Änderungsverlauf" description="Wer hat wann welche Stammdaten geändert?" />
      <div className="filter-bar">
        <label>
          <span className="label">Datentyp</span>
          <SearchSelect
            className="input w-full"
            value={entityType}
            onValueChange={(value) => {
              setEntityType(value);
              setPage(1);
            }}
          >
            <option value="">Alle Datentypen</option>
            {Object.entries(entities).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SearchSelect>
        </label>
        <label>
          <span className="label">Akteur-ID</span>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-3 text-muted" />
            <input
              className="input w-full pl-9"
              placeholder="Exakte Benutzer- oder Dienst-ID"
              value={actor}
              onChange={(event) => {
                setActor(event.target.value);
                setPage(1);
              }}
            />
          </div>
        </label>
        <label>
          <span className="label">Technischer Aktionsname</span>
          <input
            className="input w-full"
            placeholder="z. B. company.updated"
            value={action}
            onChange={(event) => {
              setAction(event.target.value);
              setPage(1);
            }}
          />
        </label>
        {(entityType || actor || action) && (
          <button
            className="button-secondary"
            onClick={() => {
              setEntityType('');
              setActor('');
              setAction('');
              setPage(1);
            }}
          >
            <X size={14} />
            Zurücksetzen
          </button>
        )}
      </div>
      <ErrorMessage error={error} />
      <div className="panel">
        {loading ? (
          <Loading>Änderungen werden geladen…</Loading>
        ) : error ? (
          <Empty>
            Der Verlauf konnte nicht geladen werden.
            <button className="button-secondary" onClick={() => setRefresh((value) => value + 1)}>
              Erneut versuchen
            </button>
          </Empty>
        ) : !items.length ? (
          <Empty>Keine Änderungen für diese Filter.</Empty>
        ) : (
          <div className="divide-y divide-line">
            {items.map((item) => (
              <div key={item.id} className="flex flex-wrap items-start gap-4 p-5">
                <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-accent/40" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{auditLabel(item)}</div>
                  <div className="mt-1 text-xs text-muted">
                    {item.actorDisplayName ?? item.actorId}
                    {auditFields(item) && ` · ${auditFields(item)}`}
                  </div>
                  {['COMPANY', 'DEVICE'].includes(item.entityType) && (
                    <Link
                      className="mt-2 inline-block text-xs text-accent"
                      to={`/${item.entityType === 'COMPANY' ? 'companies' : 'devices'}/${item.entityId}`}
                    >
                      Datensatz öffnen →
                    </Link>
                  )}
                  <details className="mt-2 text-xs text-muted">
                    <summary className="cursor-pointer">Technische Details</summary>
                    <div className="mt-2 break-all">
                      {item.action}
                      <br />
                      {item.entityId}
                      <br />
                      Akteur: {item.actorId}
                    </div>
                  </details>
                </div>
                <time className="text-xs text-muted">
                  {new Date(item.createdAt).toLocaleString('de-DE')}
                </time>
              </div>
            ))}
          </div>
        )}
      </div>
      <Pagination
        page={page}
        pageSize={25}
        total={total}
        loading={loading || Boolean(error)}
        onPageChange={setPage}
      />
    </>
  );
}
